'use client'

import { useState } from 'react'

export function EventTypeSelector({ selectedType, onSelect }) {
  const EVENT_TYPES_BY_CATEGORY = {
    'Early Life': [
      {
        title: 'Birth',
        category: 'Early Life',
        description: 'Document the beginning of life\'s journey with details about birth date, location, and first moments.',
        icon: '👶',
        events: [
          'Birth Date & Time',
          'Birth Location',
          'Birth Weight & Length',
          'First Family Photo',
          'Hospital Stay'
        ]
      },
      {
        title: 'First Steps',
        category: 'Early Life',
        description: 'Capture the milestone of taking those very first steps.',
        icon: '🚶',
        events: [
          'First Attempt',
          'First Independent Steps',
          'Walking Milestone',
          'Location of First Steps',
          'Reaction & Celebration'
        ]
      },
      {
        title: 'First Words',
        category: 'Early Life',
        description: 'Record those precious first words and the stories behind them.',
        icon: '💭',
        events: [
          'First Word Spoken',
          'Context & Meaning',
          'Family Reactions',
          'Language Development',
          'Communication Milestones'
        ]
      }
    ],
    'Education': [
      {
        title: 'First Day of School',
        category: 'Education',
        description: 'Document the exciting first day at a new school',
        subheading: 'Elementary/Primary',
        icon: '🏫',
        events: [
          'School Name & Location',
          'Teacher\'s Name',
          'First Day Outfit',
          'Special Memories',
          'First Friends Made'
        ]
      },
      {
        title: 'Graduation',
        category: 'Education',
        description: 'Celebrate completing an educational milestone',
        subheading: 'Any Level',
        icon: '🎓',
        events: [
          'Graduation Date',
          'Institution Name',
          'Degree/Certificate',
          'Ceremony Details',
          'Special Recognition'
        ]
      },
      {
        title: 'Achievement',
        category: 'Education',
        description: 'Record special academic accomplishments',
        subheading: 'Awards/Honors',
        icon: '🏆',
        events: [
          'Award Name',
          'Achievement Type',
          'Recognition Date',
          'Special Recognition',
          'Impact & Significance'
        ]
      }
    ],
    'Career': [
      {
        title: 'Career Milestones',
        category: 'Career',
        description: 'Significant career achievements and promotions',
        icon: '📈'
      },
      {
        title: 'Job Changes',
        category: 'Career',
        description: 'New roles and career transitions',
        icon: '💼'
      },
      {
        title: 'Professional Achievements',
        category: 'Career',
        description: 'Awards, certifications, and recognition',
        icon: '🎯'
      }
    ],
    'Relationship': [
      {
        title: 'Family Milestones',
        category: 'Relationship',
        description: 'Significant family moments and celebrations',
        icon: '👨‍👩‍👧‍👦'
      },
      {
        title: 'Personal Relationships',
        category: 'Relationship',
        description: 'Romantic relationships and partnerships',
        icon: '💑'
      },
      {
        title: 'Social Connections',
        category: 'Relationship',
        description: 'Friendships and community bonds',
        icon: '🤝'
      }
    ],
    'Life Events': [
      {
        title: 'Home & Location',
        category: 'Life Events',
        description: 'Places you\'ve lived and moved',
        icon: '⌂',
        events: [
          'Moving to New Home',
          'First Home Purchase',
          'City/Country Relocation',
          'Home Renovation',
          'Neighborhood Change'
        ]
      },
      {
        title: 'Travel & Adventure',
        category: 'Life Events',
        description: 'Journeys and experiences',
        icon: '👁',
        events: [
          'First International Trip',
          'Extended Travel',
          'Adventure Experience',
          'Cultural Immersion',
          'Milestone Journey'
        ]
      },
      {
        title: 'General Life Events',
        category: 'Life Events',
        description: 'Other significant moments',
        icon: '+',
        events: [
          'Personal Achievement',
          'Major Life Change',
          'Special Moment',
          'Personal Growth',
          'Custom Life Event'
        ]
      }
    ]
  }

  const categories = Object.keys(EVENT_TYPES_BY_CATEGORY)
  const [selectedCategory, setSelectedCategory] = useState(categories[0])
  const eventTypes = EVENT_TYPES_BY_CATEGORY[selectedCategory] || []

  return (
    <div className="space-y-8">
      {/* Category Selector */}
      <div className="flex gap-4">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-6 py-3 rounded-full transition-all ${
              selectedCategory === category
                ? 'bg-[#556B2F] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Event Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {eventTypes.map((event) => (
          <button 
            key={event.title}
            onClick={() => onSelect(event)}
            className={`p-6 rounded-lg border-2 text-left transition-all hover:shadow-md ${
              selectedType?.title === event.title 
                ? 'border-[#556B2F] bg-[#556B2F]/5' 
                : 'border-gray-200 hover:border-[#556B2F]/50'
            }`}
          >
            {/* Icon Circle */}
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
              event.category === 'Life Events' && event.title === 'Travel & Adventure' ? 'bg-red-50' :
              'bg-green-50'
            }`}>
              <span className={`text-2xl ${
                event.category === 'Life Events' && event.title === 'Travel & Adventure' ? 'text-red-400' :
                'text-green-600'
              }`}>
                {event.icon}
              </span>
            </div>

            {/* Title and Description */}
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              {event.title}
            </h3>
            <p className="text-gray-600 mb-4">{event.description}</p>

            {/* Types of Events List - Only show for Life Events category */}
            {event.category === 'Life Events' && event.events && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  {event.title === 'General Life Events' ? 'Examples Include:' : 'Types of Events:'}
                </h4>
                <ul className="space-y-2">
                  {event.events.map((type) => (
                    <li key={type} className="text-sm text-gray-600 flex items-center">
                      <span className={`w-1.5 h-1.5 rounded-full mr-2 ${
                        event.title === 'Travel & Adventure' ? 'bg-red-400' :
                        'bg-green-500'
                      }`}></span>
                      {type}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  )
} 